import { updateHeaderWidthToSlide } from '@/modules/actions/updateHeaderWidthAction';
import { getSwiperInstance } from '@/helpers/swiperInstance';
import { applySlideScaling } from "@/modules/actions/slideScaleAction";
import { activeVideoPlay } from "@/modules/actions/videoControls";
import { setupAutoPlayNextAction } from '@/modules/actions/desktop/setupAutoPlayNextAction';
import { checkVolumeButtonState } from "@/helpers/volumeControl";
import { animateCurrentProgressBar, prepareCurrentProgressBar } from "@/modules/actions/headerLineDuration";
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { buildMediaAssetIdParam } from "@/helpers/buildMediaAssetIdParam";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import { isMediaImageReady } from '@/helpers/mediaUtils';
import { normalizeSquareContentIndex } from '@/helpers/squareContentIndex';
import { queryHeaderElements } from '@/helpers/headerShadow';
import { isReelLike } from '@/helpers/isReelLike';

export const slideClickNavigateAction = () => {
  // PC時のみクリック移動を有効化
  if (window.storeeelItems.isMobile) return;
  const swiper = getSwiperInstance();
  const allSlides = document.querySelectorAll('.storeeel-swiper-group-wrapper  .swiper-slide');
  allSlides.forEach((slide, index) => {
    slide.addEventListener('click', () => {
      const imageInSlide = slide.querySelector('.slide-media-wrapper .storeeel-media-image');
      const progressIndex = window.storeeelItems.frame === "circle" ? index : 0;
      // リール型・タイル型のloop設定を判定する
      const isSquareLoop = isReelLike(window.storeeelItems.frame) && Boolean(swiper?.params?.loop);
      const isSquareLoopRepeat = isSquareLoop && Boolean(window._storeeelPcSquareLoopRepeat);

      // アクティブスライド（特に画像）のクリックで進捗が未開始なら開始する
      if (swiper.activeIndex === index) {
        if (imageInSlide) {
          const activeWrapper = slide.querySelector('.slide-media-wrapper');
          const banners = queryHeaderElements('.storeeel-movie-select_banner');
          const targetBanner = banners[progressIndex];
          const isActive = targetBanner && targetBanner.classList.contains('active');

          if (!isActive) {
            showMediaLoadingShimmer(activeWrapper);
            if (imageInSlide._onLoadForProgress) {
              imageInSlide.removeEventListener('load', imageInSlide._onLoadForProgress);
              imageInSlide._onLoadForProgress = null;
            }
            if (imageInSlide._onErrorForProgress) {
              imageInSlide.removeEventListener('error', imageInSlide._onErrorForProgress);
              imageInSlide._onErrorForProgress = null;
            }

            const cleanup = () => {
              if (imageInSlide._onLoadForProgress) {
                imageInSlide.removeEventListener('load', imageInSlide._onLoadForProgress);
                imageInSlide._onLoadForProgress = null;
              }
              if (imageInSlide._onErrorForProgress) {
                imageInSlide.removeEventListener('error', imageInSlide._onErrorForProgress);
                imageInSlide._onErrorForProgress = null;
              }
            };

            const startProgress = () => {
              prepareCurrentProgressBar(progressIndex);
              animateCurrentProgressBar(5, progressIndex);
              hideMediaLoadingShimmer(activeWrapper);
              setupAutoPlayNextAction();
              cleanup();
            };

            const tryStartProgress = () => {
              if (!isMediaImageReady(imageInSlide)) return false;
              startProgress();
              return true;
            };

            // 読み込み完了後に進捗を開始する
            imageInSlide._onLoadForProgress = () => { tryStartProgress(); };
            imageInSlide._onErrorForProgress = () => {
              hideMediaLoadingShimmer(activeWrapper);
              cleanup();
            };
            imageInSlide.addEventListener('load', imageInSlide._onLoadForProgress);
            imageInSlide.addEventListener('error', imageInSlide._onErrorForProgress);
            tryStartProgress();
          } else {
            hideMediaLoadingShimmer(activeWrapper);
          }
        }
        return;
      }
      if (swiper && typeof swiper.slideTo === 'function') {
        // リール型・タイル型を同一挙動として扱う
        const isSquare = isReelLike(window.storeeelItems.frame);
        const isNextSlide = slide.classList.contains('swiper-slide-next');
        const isPrevSlide = slide.classList.contains('swiper-slide-prev');
        // PC + リール型の少数コンテンツでは、隣スライドへ方向を固定して移動する
        if (isSquareLoopRepeat && (isNextSlide || isPrevSlide)) {
          const isNext = isNextSlide;
          const currentIndex = (swiper?.params?.loop && Number.isFinite(swiper.realIndex))
            ? swiper.realIndex
            : Number(window.moviePosition || 0);
          window.moviePosition = normalizeSquareContentIndex(currentIndex + (isNext ? 1 : -1));
          if (isNext && typeof swiper.slideNext === 'function') {
            swiper.slideNext();
          } else if (!isNext && typeof swiper.slidePrev === 'function') {
            swiper.slidePrev();
          } else {
            swiper.slideTo(index);
          }
        } else if (isSquareLoop && typeof swiper.slideToLoop === 'function') {
          // ループ環境では DOM 上の index が複製スライドを含むため、real index へ正規化する
          const raw = slide.getAttribute('data-swiper-slide-index');
          const realIndex = raw == null ? 0 : Number(raw);
          swiper.slideToLoop(realIndex);
          window.moviePosition = isSquare ? normalizeSquareContentIndex(realIndex) : realIndex;
        } else {
          swiper.slideTo(index);
          window.moviePosition = isSquare ? normalizeSquareContentIndex(index) : index;
        }

        // PC + リール型・タイル型は slideChange イベント側で進捗/計測を active スライド基準に同期する
        if (isReelLike(window.storeeelItems.frame)) {
          return;
        }

        applySlideScaling();
        updateHeaderWidthToSlide();

        const banners = queryHeaderElements('.storeeel-movie-select_banner');
        banners.forEach((b, i) => b.style.padding = i === index ? '2px' : '');

        // スライドの動画再生
        activeVideoPlay();
        // 音量ボタンの状態に応じて動画のミュート変更
        checkVolumeButtonState();
        // 動画終了後の次の動画を再生
        setupAutoPlayNextAction();

        // スライドクリック後の進捗バー開始（再生/読み込み完了後）
        const activeWrapper = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper');
        showMediaLoadingShimmer(activeWrapper);
        const video = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper video.storeeel-movie');
        const image = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active .slide-media-wrapper .storeeel-media-image');
        if (video && video.style.display !== 'none') {
          if (video._onLoadedMetadataForProgress) {
            video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
            video._onLoadedMetadataForProgress = null;
          }
          if (video._onPlayingForProgress) {
            video.removeEventListener('playing', video._onPlayingForProgress);
            video._onPlayingForProgress = null;
          }

          const cleanup = () => {
            if (video._onLoadedMetadataForProgress) {
              video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
              video._onLoadedMetadataForProgress = null;
            }
            if (video._onPlayingForProgress) {
              video.removeEventListener('playing', video._onPlayingForProgress);
              video._onPlayingForProgress = null;
            }
          };

          const tryStartProgress = () => {
            const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
            if (!currentSlide || !currentSlide.contains(video)) {
              cleanup();
              hideMediaLoadingShimmer(activeWrapper);
              return false;
            }
            if (video.paused || video.ended) return false;
            if (video.readyState < 2) return false;
            const duration = Number(video.duration);
            if (!Number.isFinite(duration) || duration <= 0) return false;
            animateCurrentProgressBar(duration, progressIndex);
            hideMediaLoadingShimmer(activeWrapper);
            cleanup();
            return true;
          };

          // 再生開始まで進捗を開始しない
          prepareCurrentProgressBar(progressIndex);
          video._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
          video._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
          video.addEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video.addEventListener('playing', video._onPlayingForProgress);
          tryStartProgress();
        } else if (image) {
          if (image._onLoadForProgress) {
            image.removeEventListener('load', image._onLoadForProgress);
            image._onLoadForProgress = null;
          }
          if (image._onErrorForProgress) {
            image.removeEventListener('error', image._onErrorForProgress);
            image._onErrorForProgress = null;
          }

          const cleanup = () => {
            if (image._onLoadForProgress) {
              image.removeEventListener('load', image._onLoadForProgress);
              image._onLoadForProgress = null;
            }
            if (image._onErrorForProgress) {
              image.removeEventListener('error', image._onErrorForProgress);
              image._onErrorForProgress = null;
            }
          };

          const tryStartProgress = () => {
            const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
            if (!currentSlide || !currentSlide.contains(image)) {
              cleanup();
              hideMediaLoadingShimmer(activeWrapper);
              return false;
            }
            if (!isMediaImageReady(image)) return false;
            animateCurrentProgressBar(5, progressIndex);
            hideMediaLoadingShimmer(activeWrapper);
            cleanup();
            return true;
          };

          // 読み込み完了後に進捗を開始する
          prepareCurrentProgressBar(progressIndex);
          image._onLoadForProgress = () => { tryStartProgress(); };
          image._onErrorForProgress = () => {
            hideMediaLoadingShimmer(activeWrapper);
            cleanup();
          };
          image.addEventListener('load', image._onLoadForProgress);
          image.addEventListener('error', image._onErrorForProgress);
          tryStartProgress();
        }

        // 計測とローカルストレージ保存
        let mediaName;
        let mediaId;
        let groupId;
        if (video) {
          mediaName = video.getAttribute('media-name');
          mediaId = video.getAttribute('media-id');
        } else if (image) {
          mediaName = image.getAttribute('media-name');
          mediaId = image.getAttribute('media-id');
        }
        if (window.storeeelItems.frame === "circle") {
          groupId = window.iconId;
        } else {
          // リール型・タイル型はgroupIdをmediaViewログに記録しない
          groupId = null;
        }
        const mediaAssetType = activeWrapper?.dataset?.assetType;
        const isFreqInsert = activeWrapper?.dataset?.isFrequencyInsertContent === 'true';
        if (isFreqInsert) {
          const freqContentId = activeWrapper?.dataset?.frequencyInsertContentId || mediaId;
          trackStoreeelEvent('storeeel_frequency_insert_media_view', {
            storeeel_name: mediaName,
            ...buildMediaAssetIdParam(freqContentId, mediaAssetType),
            campaign_id: window.storeeelItems.id,
            ...(groupId ? { group_id: groupId } : {}),
          });
        } else {
          trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
            storeeelName: mediaName,
            groupId,
            mediaId,
            campaignId: window.storeeelItems.id,
            mediaAssetType,
          }));
        }
        localStorageRepositories.get("mediaView").set(
          mediaId,
          window.storeeelItems.id,
          groupId,
          mediaAssetType
        );
        // 無期限の既読フラグも保存（リール型・タイル型は保存しない）
        try {
          if (!isReelLike(window.storeeelItems?.frame)) {
            const contentId = (video && video.getAttribute('content-id')) || (image && image.getAttribute('content-id'));
            if (contentId) {
              localStorageRepositories.get("contentSeen").set(contentId);
            }
            if (window.iconId && window.storeeelItems.frame === 'circle') {
              updateGroupSeenBorder(window.iconId);
            }
          }
        } catch { void 0; }
      }
    });
  });
};
